import { cn } from '@/lib/cn';
import { SIGN_IN_URL } from '../lib/links';
import type { ReportSection as ReportSectionData } from '../lib/reports';
import checkmarkCircle from '../assets/v2-checkmark-circle.svg';
import arrowDown from '../assets/v2-arrow-down.svg';

/**
 * One report section — the shared layout behind Figma nodes 1:409 / 1:452 /
 * 1:491 / 1:530 / 1:568 / 1:607.
 *
 * Exact design values (at the 1920 reference width):
 *  · band       image-left variants sit on rgba(246,246,246,0.76), full-bleed
 *  · padding    px-133 py-68; content row 1654 wide
 *
 * The row is `max-w-[1782px]` with `xl:px-16`, not `max-w-[1654px]` with
 * `2xl:px-0`. 1654 + 2×64 = 1782, so at 1920 the centred box leaves a 69px
 * margin and the padding adds 64 — landing content at x=133, 1654 wide, exactly
 * as designed. The earlier `2xl:px-0` collapsed the padding to ZERO between 1536
 * (where 2xl starts) and 1654 (where max-w finally creates its own margin), so
 * the panel sat flush against the viewport edge through that whole range.
 *  · panel      592×486, gradient-to-top + 2px border on top/left/bottom, r-8
 *  · image      430×427 at (162, 59) inside the panel — flush to its
 *               bottom-right corner, leaving a 162px gradient strip on the left
 *               and 59px on top. The 4px white border is baked into the export.
 *  · pill       NOT rendered — `div.tagline` is hidden on all nine sections in
 *               the 2026-07 design (nodes 2:94, 2:142, 2:175, 2:219, 2:253,
 *               2:297, 2:330, 2:374, 2:407). Its stale text is still in the file
 *  · heading    Inter 700 48/48, #202939
 *  · copy       Inter 400 18/24, #697586
 *  · bullets    column gap-14, row gap-12, COLUMN gap-60, 24px check icon,
 *               Inter 500 20/24
 *  · CTAs       gap-16; "Get started" white/1px #364152 r-full px-25 py-12,
 *               "Learn more" + 16px arrow rotated to point right
 *
 * The image is positioned in PERCENTAGES derived from those pixel values, so the
 * panel reproduces the design exactly at any width without a media query.
 *
 * Both CTAs link to the SSO sign-in (agreed): every report CTA converts to a
 * sign-in rather than pointing at pages that do not exist.
 */

/** 430/592 and 427/486 → the image is flush to the panel's bottom-right. */
const IMAGE_GEOMETRY = {
  left: `${(162 / 592) * 100}%`,
  top: `${(59 / 486) * 100}%`,
  width: `${(430 / 592) * 100}%`,
  height: `${(427 / 486) * 100}%`,
} as const;

function GradientPanel({ section }: { section: ReportSectionData }) {
  return (
    <div
      className={cn(
        // 592×486 at the design width; scales down fluidly, aspect locked.
        'relative w-full max-w-[592px] shrink-0 overflow-hidden rounded-lg',
        'aspect-[592/486] border-b-2 border-l-2 border-t-2 border-solid',
        'bg-gradient-to-t',
        section.frameClass,
      )}
      data-node={section.node}
    >
      <img
        src={section.image}
        alt={section.imageAlt}
        loading="lazy"
        decoding="async"
        width={430}
        height={427}
        className="absolute max-w-none rounded-tl-lg"
        style={IMAGE_GEOMETRY}
      />
    </div>
  );
}

function TextColumn({ section }: { section: ReportSectionData }) {
  const ctaClass =
    'text-base font-medium leading-6 text-landing-outline transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-landing-outline';

  return (
    <div className="flex w-full flex-col gap-8 lg:min-w-0 lg:flex-1 lg:gap-10">
      <div className="flex flex-col gap-5 lg:gap-[26px]">
        <h2 className="text-[28px] font-bold leading-[1.15] text-landing-slate sm:text-[34px] lg:text-[40px] xl:text-[44px] xl:leading-[44px] 2xl:text-5xl 2xl:leading-[48px]">
          {section.heading}
        </h2>

        <div className="flex flex-col gap-6 lg:gap-8">
          <p className="text-base leading-6 text-landing-pale lg:text-lg">{section.copy}</p>

          {/* Bullet columns — 1 or 2 depending on the section. Each column is
              its own list; they sit side by side from `sm` up. */}
          <div className="flex flex-col gap-3.5 sm:flex-row sm:gap-x-[60px]">
            {section.bulletColumns.map((column, columnIndex) => (
              <ul key={columnIndex} className="flex min-w-0 flex-1 flex-col gap-3.5">
                {column.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <img
                      src={checkmarkCircle}
                      alt=""
                      width={24}
                      height={24}
                      className="mt-0.5 size-6 shrink-0"
                    />
                    <span className="text-base font-medium leading-6 text-landing-slate lg:text-lg xl:text-xl">
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            ))}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <a
          href={SIGN_IN_URL}
          className={cn(
            ctaClass,
            'flex items-center justify-center rounded-full border border-solid border-landing-outline bg-white px-[25px] py-3 hover:bg-landing-outline hover:text-white',
          )}
        >
          Get started
        </a>
        <a href={SIGN_IN_URL} className={cn(ctaClass, 'flex items-center gap-2 hover:opacity-70')}>
          Learn more
          <img
            src={arrowDown}
            alt=""
            width={16}
            height={16}
            className="size-4 -rotate-90"
            aria-hidden="true"
          />
        </a>
      </div>
    </div>
  );
}

export function ReportSection({ section }: { section: ReportSectionData }) {
  const imageLeft = section.imageSide === 'left';

  return (
    <section
      id={section.id}
      className={cn('w-full scroll-mt-16 lg:scroll-mt-24', imageLeft && 'bg-landing-band')}
    >
      <div
        className={cn(
          'mx-auto flex max-w-[1782px] flex-col items-center gap-10 px-5 py-12',
          'sm:px-6 md:px-8 lg:flex-row lg:gap-16 lg:px-10 lg:py-[68px] xl:px-16',
          // Text always comes first in the DOM so the reading order is correct on
          // mobile; the visual swap happens only once the row is side-by-side.
          imageLeft && 'lg:flex-row-reverse',
        )}
      >
        <TextColumn section={section} />
        <GradientPanel section={section} />
      </div>
    </section>
  );
}
